import { type AgentSelectionCandidate } from './AgentSelectionCandidate';
import { isDefaultAgentSelection } from './isDefaultAgentSelection';

/**
 * Resolves the agent metadata that should drive the input-hat UI for the
 * next message, or `undefined` when no input hat should be shown.
 *
 * @param selectedAgentCandidate Resolved candidate for the currently-active
 *   selected-agent override.
 * @param activeSelectedAgentId The currently-active selected-agent id, as
 *   returned by `useActiveSelectedAgentId`.
 * @param currentAgent The conversation agent currently associated with this conversation.
 */
export const getInputHatSelectedAgent = <TAgent extends AgentSelectionCandidate>(
	selectedAgentCandidate: TAgent | undefined,
	activeSelectedAgentId: string | undefined,
	currentAgent: AgentSelectionCandidate | undefined,
): TAgent | undefined => {
	if (!activeSelectedAgentId) {
		return undefined;
	}

	if (selectedAgentCandidate?.id !== activeSelectedAgentId) {
		return undefined;
	}

	if (isDefaultAgentSelection(selectedAgentCandidate)) {
		// Selecting the default Rovo agent ("Switch to Rovo") is a meaningful override only in an
		// agentic (dedicated-agent) chat, where it switches the next message away from the
		// conversation's agent — so surface it as a dismissable hat there. In a standard Rovo chat
		// the baseline is already the default agent, so the hat would be a redundant no-op.
		return isDefaultAgentSelection(currentAgent) ? undefined : selectedAgentCandidate;
	}

	return selectedAgentCandidate.id === currentAgent?.id ? undefined : selectedAgentCandidate;
};
